import AnalyticsService from '~/libs/analytics-service'
import { localStorageService } from "~/libs/local-storage.service"
import { useProgressStore } from '~/store/progress'
import { useRootStore } from '~/store/root'
import { useBorrowerStore } from '~/store/borrower'
import { useExperienceStore } from '~/store/experience'
import { useAppAnalyticsStore } from '~/store/app-analytics'
import { useExperimentsStore } from '~/store/experiments'

export default defineNuxtPlugin(({ $lendioCookies, $axios }) => {
  const appAnalyticsStore = useAppAnalyticsStore()
  const progressStore = useProgressStore()
  const borrowerStore = useBorrowerStore()
  const rootStore = useRootStore()
  const experienceStore = useExperienceStore()
  const experimentsStore = useExperimentsStore()
  const router = useRouter()

  function lendioPerformanceBeginLoadingTime() {
    if (!window.LendioPerformanceLogging) {
      return
    }

    try {
      window.LendioPerformanceLogging.baseRouteChangeStart = {
        startTime: new Date(),
        logged: false
      }

      window.LendioPerformanceLogging.beginLoadingTime = window.LendioPerformanceLogging.calculateLoadTime()
    } catch (err) {
      log.error('LendioPerformanceLogging Error at router.beforeEach: ', err)
    }
  }

  function lendioPerformanceRouteChangeTime(){
    if (!window.LendioPerformanceLogging) {
      return
    }

    try {
      window.LendioPerformanceLogging.routeChangeTime = window.LendioPerformanceLogging.calculateLoadTime(window.LendioPerformanceLogging.baseRouteChangeStart.startTime)
    } catch (err) {
      log.error('LendioPerformanceLogging Error at router.afterEach: ', err)
    }
  }
  function getABExperimentStr() {
    if (!experimentsStore.loadedActiveExperiments) {
      return null
    }
    const MAX_PARAM_LENGTH = 512
    const abExperiments = (experimentsStore.activeUserExperiments || [])
      .filter((item) => item?.experiment?.type === 'ab')
      .map((item) => `${item.experimentId}-${item.group}`)

    if (!abExperiments.length) {
      return null
    }

    const DELIMITER = '|'
    let abExperimentStr = DELIMITER

    for (const experimentKey of abExperiments) {
      const next = `${abExperimentStr}${experimentKey}${DELIMITER}`
      if (next.length > MAX_PARAM_LENGTH) {
        log.warning(
          'Unable to set full AB experiment list to FS.',
          {
            abExperiments: abExperiments.join(DELIMITER),
            reason: `${MAX_PARAM_LENGTH}-character limit reached`
          }
        );
        break;
      }
      abExperimentStr = next;
    }
    return abExperimentStr
  }

  // handle experience redirect
  router.beforeEach(async (to, from, next) => {
    // Log page load performance
    lendioPerformanceBeginLoadingTime()
    next()
  })

  /*
  * Remove any tooltip popper nodes Bootstrap appended to <body> that outlived their component.
  * Components dispose their own instances in beforeUnmount, this catches anything left so
  * a tap-opened tooltip can't block content on the next page.
  */
  router.afterEach(() => {
    document.querySelectorAll('.tooltip').forEach(tooltipNode => tooltipNode.remove())
  })

  /*
  * Every time the route changes (fired on initialization too)
  */
  router.afterEach((to, from) => {
    lendioPerformanceRouteChangeTime()
    progressStore.navigating = null
    progressStore.pushVisitedRoute(to.path.replace(/\/?$/, ''))
    borrowerStore.getBorrower().then(async borrower => {
      await appAnalyticsStore.waitForService('dreams')
      AnalyticsService.trackRoute(borrower ? borrower.id : null, appAnalyticsStore.getLendioId, to.fullPath, $axios)

      if (borrower) {
        // Custom fields must named following the pattern 'ident_type' (https://help.fullstory.com/hc/en-us/articles/4446290296599-Setting-custom-API-properties)
        let data = {
          'displayName': borrower.name,
          'borrowerId_int': borrower.id,
          'clientId_int': borrower.clientId,
          'containerUrl_str': containerUrl(),
          'isTest_bool': !!borrower.isTest,
          'mineralGroup_str': borrower.mineralGroup ?? '',
        }

        const email = rootStore.authUser?.email

        if (email) {
          data['email'] = email
        }

        if (borrower.created) {
          data['createdDate_date'] = new Date(borrower.created)
        }

        if (experienceStore.currentExperimentName) {
          data['experience_str'] = experienceStore.currentExperimentName
        }

        const abExperimentsStr = getABExperimentStr()
        if (abExperimentsStr) {
          data['abExperiments_str'] = abExperimentsStr
        }

        AnalyticsService.identify(appAnalyticsStore.getLendioId, data) // Identify the borrower data to Fullstory
      }
    })

    let redirect = $lendioCookies.get('redirect')
    let redirectURI = localStorageService.getItem('redirectURI')
    let path = to.path

    if (path === redirect) {
      $lendioCookies.remove('redirect')
    }

    if (path === redirectURI) {
      localStorageService.removeItem('redirectURI')
    }
  })

  /**
   * Return the "container URL". Used to enable segmentation of FullStory users in
   * different embedded contexts. For example, an Intelligent Lending user
   * on Ridgeline Bank would return "ridgelinebank.lendio.com"
   *
   * @returns String
   */
  function containerUrl() {
    return window.self === window.top
      ? window.location.host
      : window.document.referrer || 'unknown';
  }
})
